/**
 * Requests the HTML head starts before the app's JavaScript has loaded (the
 * inline script in index.html): the free-trial switches on every page, and on
 * the home page the ticker, market snapshot and plans. The app's first GET of
 * the same URL takes that answer instead of asking again; later ones
 * (refetches) go to the network as usual.
 */
interface Prefetched {
  /** performance.now() when the request started. */
  at: number;
  /** The parsed JSON, or null when the request failed (the app then asks itself). */
  p: Promise<unknown>;
}

declare global {
  interface Window {
    __raiPre?: Record<string, Prefetched>;
  }
}

/** An answer older than this is not reused: the page was open a while before it asked. */
const MAX_AGE_MS = 15_000;

/** The prefetched answer for this URL, once; undefined when there is none (or it's too old). */
export function takePrefetched(url: string): Promise<unknown> | undefined {
  const all = typeof window === "undefined" ? undefined : window.__raiPre;
  const hit = all?.[url];
  if (!all || !hit) return undefined;
  Reflect.deleteProperty(all, url); // once: later requests go to the network
  return performance.now() - hit.at < MAX_AGE_MS ? hit.p : undefined;
}
